import React, { forwardRef } from 'react';
import styled from 'styled-components';
import SlidesEmbed from './SlidesEmbed';
import VideoPlayer from './VideoPlayer';
import ImageViewer from './ImageViewer';
import ControlledSlides from './ControlledSlides';
import PanoramaViewer, { yawForScreenCode } from './PanoramaViewer';
import CubePhysicsView from './CubePhysicsView';
import KuutioCubeGradient from '../KuutioCubeGradient';
import { getSocketUrl } from '../../config/api';

const BlankScreen = styled.div`
  width: 100%;
  height: 100%;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/** A wall showing nothing still shows the cube — the same living object as the
 *  welcome wall, not a flat logo. A 4K panel makes a static SVG look like a
 *  crashed screensaver. */
const BlankCube = () => (
  <BlankScreen>
    <KuutioCubeGradient size={700} />
  </BlankScreen>
);

const KuutioLogo = styled.img`
  width: 200px;
  height: 200px;
  object-fit: contain;
`;

const UnknownType = styled.div`
  width: 100%;
  height: 100%;
  background: #1a1a2e;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 1.2rem;
`;

/**
 * MediaPlayer - Routes to the appropriate media component based on content type
 *
 * @param {object} content - Content configuration object
 * @param {string} content.type - Content type: 'slides', 'controlled-slides', 'video', 'image', 'blank'
 *
 * For type: 'slides' (iframe embed, self-controlled)
 * @param {string} content.slidesUrl - Google Slides URL
 * @param {boolean} content.autoStart - Auto-start slideshow
 * @param {boolean} content.slideLoop - Loop slides
 * @param {number} content.delayMs - Auto-advance delay (0 = manual)
 * @param {number} content.startSlide - Starting slide number
 *
 * For type: 'controlled-slides' (Flow Manager controlled)
 * @param {string} content.presentationId - Cached presentation UUID or Google ID
 * @param {string} content.instanceId - Instance ID for socket events
 * @param {boolean} content.showCounter - Show slide counter (default: true)
 * @param {boolean} content.showNotes - Show speaker notes (default: false)
 *
 * For type: 'video'
 * @param {string} content.videoUrl - Video URL
 * @param {boolean} content.autoplay - Auto-play video
 * @param {boolean} content.loop - Loop video
 * @param {boolean} content.muted - Mute video
 * @param {boolean} content.showControls - Show video controls
 *
 * For type: 'image'
 * @param {string} content.imageUrl - Image URL
 * @param {string} content.imageMode - Display mode: 'fit', 'fill', 'center'
 */
const MediaPlayer = forwardRef(({ content, screenCode = null, clockOffset = 0, socket = null, instanceId = null, lite = false }, ref) => {
  if (!content) return <BlankCube />;

  switch (content.type) {
    case 'slides':
      return (
        <SlidesEmbed
          url={content.slidesUrl}
          autoStart={content.autoStart}
          loop={content.slideLoop}
          delayMs={content.delayMs}
          startSlide={content.startSlide}
        />
      );

    case 'controlled-slides':
      return (
        <ControlledSlides
          presentationId={content.presentationId}
          instanceId={content.instanceId}
          initialSlideIndex={content.currentSlideIndex}
          showCounter={content.showCounter}
          showNotes={content.showNotes}
        />
      );

    case 'video':
      return (
        <VideoPlayer
          ref={ref}
          url={content.videoUrl}
          autoplay={content.autoplay !== false}
          loop={content.loop}
          muted={content.muted}
          showControls={content.showControls}
          syncEpoch={content.syncEpoch}
          clockOffset={clockOffset}
        />
      );

    case 'image':
      return (
        <ImageViewer
          url={content.imageUrl}
          mode={content.imageMode}
        />
      );

    case 'cube-physics':
      // One shared cube roaming all 8 screens; taps steer it (world state
      // lives on the server, simulated locally on the shared clock)
      return (
        <CubePhysicsView
          screenCode={screenCode}
          socket={socket}
          clockOffset={clockOffset}
          instanceId={instanceId}
        />
      );

    case 'tiled-360':
      // Pre-cropped per-screen tiles: full panel resolution, hardware H.264
      // decode, plain <video> — no WebGL sphere. Made by scripts/make-360-tiles.sh
      return (
        <VideoPlayer
          ref={ref}
          url={`${getSocketUrl()}/public/tiles/${content.tilesPath}/${screenCode || 'A1'}.mp4`}
          autoplay
          loop={content.loop !== false}
          muted={content.muted !== false}
          syncEpoch={content.syncEpoch}
          clockOffset={clockOffset}
        />
      );

    case '360-image':
    case '360-video':
      // Each screen renders its wall-direction slice of the shared panorama
      return (
        <PanoramaViewer
          imageUrl={content.imageUrl}
          videoUrl={content.type === '360-video'
            ? (lite && content.liteVideoUrl) || content.videoUrl
            : null}
          yaw={yawForScreenCode(screenCode, content.yawOffset || 0)}
          hfov={content.hfov || 45}
          syncEpoch={content.syncEpoch}
          clockOffset={clockOffset}
          lite={lite}
          playbackRate={content.playbackRate || 1}
        />
      );

    case 'blank':
      return <BlankCube />;

    default:
      return (
        <UnknownType>
          Tuntematon mediatyyppi: {content.type}
        </UnknownType>
      );
  }
});

MediaPlayer.displayName = 'MediaPlayer';

export default MediaPlayer;
